import { useState } from 'react';
import type { TechStackBlock as TechStackBlockType } from '@/shared/types/blocks';
import { ToolIcon, type ToolName } from '@/shared/ui/tool-icon';

/** S3 assets base URL */
const ASSETS_CDN_BASE = 'https://assets.ax.co.kr/';

/** 상대 경로를 절대 URL로 변환 (S3 키 → 전체 URL) */
function resolveAssetUrl(path: string): string {
  if (!path) return path;
  // 이미 절대 URL이면 그대로 반환
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('/')) {
    return path;
  }
  // S3 키인 경우 base URL 추가
  return `${ASSETS_CDN_BASE}${path}`;
}

/** 개별 Tool 아이콘 컴포넌트 (이미지 로드 실패 시 fallback 지원) */
function ToolIconWithFallback({ tool }: { tool: TechStackBlockType['tools'][number] }) {
  const [imgError, setImgError] = useState(false);

  // inline SVG 문자열인 경우 (서버에서 제공하는 신뢰할 수 있는 데이터)
  if (tool.svg?.startsWith('<svg')) {
    // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted SVG from server config
    return <div dangerouslySetInnerHTML={{ __html: tool.svg }} className="w-5 h-5" />;
  }

  // 이미지 URL이 있고 로드 성공한 경우
  if (tool.svg && !imgError) {
    const imageUrl = resolveAssetUrl(tool.svg);
    return (
      <img src={imageUrl} alt={tool.name} className="w-5 h-5" onError={() => setImgError(true)} />
    );
  }

  // fallback: ToolIcon 컴포넌트 사용
  return <ToolIcon name={tool.icon as ToolName} size="sm" />;
}

/**
 * TechStackBlock - 기술 스택/도구 목록
 *
 * Figma 디자인 기준:
 * - 아이템 간격: gap-3 (12px)
 * - 라벨: label-smaller (12px Regular)
 * - 아이콘 영역: 28x28px (w-7 h-7)
 */
export function TechStackBlock({ tools }: Omit<TechStackBlockType, 'type' | 'id'>) {
  return (
    <div className="flex items-center gap-3">
      {tools.map((tool) => (
        <div
          key={tool.id}
          className="rounded-full border-label-disabled bg-label-disabled overflow-hidden flex items-center gap-1.5 p-1 pr-3"
        >
          <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center">
            <ToolIconWithFallback tool={tool} />
          </div>
          <span className="label-smaller text-black">{tool.name}</span>
        </div>
      ))}
    </div>
  );
}
